if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        // Für evtl. spätere Disconnects
        this.__engravingObserver = null;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      /* ===== Helpers: Engraving-Rebind zuverlässig ausführen ===== */
      _rebindEngraving(root) {
        try {
          if (window.__engravingRebind) {
            window.__engravingRebind(root || this.modalContent);
          }
        } catch (e) { /* no-op */ }
      }

      _watchPaymentButtons(root) {
        // alten Observer abschalten
        if (this.__engravingObserver) {
          try { this.__engravingObserver.disconnect(); } catch(_) {}
          this.__engravingObserver = null;
        }
        const scope = root || this.modalContent;
        if (!scope) return;

        // Wenn Shopify die Payment-Buttons neu rendert (childList-Changes) -> Rebind
        const obs = new MutationObserver((muts) => {
          // Nur reagieren, wenn Payment-Button-Struktur betroffen ist
          const changed = muts.some(m =>
            (m.addedNodes && [...m.addedNodes].some(n =>
              n.nodeType === 1 && (
                n.matches?.('.shopify-payment-button, .shopify-payment-button__button, [data-shopify="payment-button"]') ||
                n.querySelector?.('.shopify-payment-button, .shopify-payment-button__button, [data-shopify="payment-button"]')
              )
            )) ||
            (m.target && (m.target.matches?.('.shopify-payment-button, [data-shopify="payment-button"]')))
          );
          if (changed) {
            // minimal delay, damit DOM stabil ist
            setTimeout(() => this._rebindEngraving(scope), 50);
          }
        });

        // Beobachte nur innerhalb des Produktinfos im Modal
        obs.observe(scope, { childList: true, subtree: true, attributes: false });
        this.__engravingObserver = obs;

        // Sofort einmalig binden (falls Button schon da ist)
        this._rebindEngraving(scope);
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        // Observer säubern
        if (this.__engravingObserver) {
          try { this.__engravingObserver.disconnect(); } catch(_) {}
          this.__engravingObserver = null;
        }

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            // 1) Direkt nach dem Einfügen: Gravur-Logik aktivieren
            this._rebindEngraving(this.modalContent);

            // 2) Event für andere Skripte
            document.dispatchEvent(new CustomEvent('quickadd:rendered', { detail: { container: this.modalContent }}));

            // 3) Shopify Payment Buttons initialisieren (PayPal etc.)
            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            // 4) Egal ob (re)gerendert wird: Payment-Buttons beobachten & bei Änderungen Rebind
            this._watchPaymentButtons(this.modalContent);

            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}

/* === Quick-Add: dezente Swipe-Pfeile (mobil) automatisch einfügen === */
document.addEventListener('quickadd:rendered', (e) => {
  const root = e.detail?.container;
  if (!root) return;

  const gallery =
    root.querySelector('.product__media-wrapper') ||
    root.querySelector('.slider.slider--mobile') ||
    root.querySelector('.product__media-list');

  if (!gallery) return;

  gallery.classList.add('swipe-hint-arrows');

  if (!gallery.querySelector('.swipe-arrow.left')) {
    const left = document.createElement('div');
    left.className = 'swipe-arrow left';
    left.setAttribute('aria-hidden', 'true');
    gallery.appendChild(left);
  }
  if (!gallery.querySelector('.swipe-arrow.right')) {
    const right = document.createElement('div');
    right.className = 'swipe-arrow right';
    right.setAttribute('aria-hidden', 'true');
    gallery.appendChild(right);
  }
});

/* === Quick-Add: Pfeile klickbar machen === */
document.addEventListener('quickadd:rendered', (e) => {
  const root = e.detail?.container;
  if (!root) return;

  const slider = root.querySelector('.slider.slider--mobile');
  if (!slider) return;

  const arrows = root.querySelectorAll('.swipe-arrow');
  arrows.forEach((arrow) => {
    arrow.addEventListener('click', () => {
      const itemWidth = slider.querySelector('.product__media-item')?.offsetWidth || slider.offsetWidth;

      if (arrow.classList.contains('left')) {
        slider.scrollBy({ left: -itemWidth, behavior: 'smooth' });
      } else if (arrow.classList.contains('right')) {
        slider.scrollBy({ left: itemWidth, behavior: 'smooth' });
      }
    });
  });
});
